import { Fragment, useEffect } from "react";
import imgHeroBg from "../../assets/new_ui_img/technology_banner.jpg";
import imgStep1 from "../../assets/new_ui_img/howit_1.svg";
import imgStep2 from "../../assets/new_ui_img/howit_2.svg";
import imgStep3 from "../../assets/new_ui_img/howit_3.svg";
import imgStep4 from "../../assets/new_ui_img/howit_4.svg";
import imgStep5 from "../../assets/new_ui_img/howit_5.svg";
import imgFleetCard from "../../assets/new_ui_img/howit_image.png";
import imgMaximize from "../../assets/new_ui_img/maximize.png";
import imgShape from "../../assets/new_ui_img/shape.jpg";
import imgUnlock from "../../assets/new_ui_img/unlock.jpg";
import imgStay from "../../assets/new_ui_img/stay.jpg";
import imgTechUnlock from "../../assets/new_ui_img/tech_unlock_illustration.svg";
import imgArrowRight from "../../assets/new_ui_img/arrow_right.svg";
import imgArrowLeft from "../../assets/new_ui_img/arrow_left.svg";
import imgArrowDown from "../../assets/new_ui_img/arrow_down.svg";
import iconUtilization from "../../assets/new_ui_img/icon_utilization.svg";
import iconReduceCosts from "../../assets/new_ui_img/icon_reduce_costs.svg";
import iconScaleInfra from "../../assets/new_ui_img/icon_scale_infrastructure.svg";
import iconApiLocations from "../../assets/new_ui_img/icon_api_locations.svg";
import iconGroupNetwork from "../../assets/new_ui_img/icon_group_network.svg";
import iconLeafFuture from "../../assets/new_ui_img/icon_leaf_future.svg";
import iconBarChart from "../../assets/new_ui_img/icon_bar_chart_revenue.svg";
import iconControlShield from "../../assets/new_ui_img/icon_control_shield.svg";
import { Header, Footer, ScrollToTop } from "./components/Header";

// ─── Scroll reveal ─────────────────────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.07, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".tech-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const STEPS = [
  { num: "1", title: "Create Your Account", desc: "Sign up securely with two-factor authentication.", img: imgStep1 },
  { num: "2", title: "Add Your Property", desc: "Add your property via address.", img: imgStep2 },
  { num: "3", title: "Select Your Parking Spaces", desc: "Choose the parking spaces you'd like to make available to autonomous fleets.", img: imgStep3 },
  { num: "5", title: "Set Your Availability", desc: "Define the days and hours your spaces are available.", img: imgStep4 },
  { num: "4", title: "Earn Revenue", desc: "Earn recurring revenue when fleets utilize your designated parking spaces via GeoParking Platform.", img: imgStep5 },
];

function StepCard({ num, title, desc, img }) {
  return (
    <div className="flex gap-[16px] items-start">
      <div className="relative left-0 top-[-8px] drop-shadow-[0px_1px_1px_rgba(0,0,0,0.25)] flex items-center justify-center rounded-full size-[30px]"
          style={{ background: "radial-gradient(circle at 50% 50%, #ffffff 0%, #f4f4f4 100%)", border: "1px solid #388e3c" }}>
          <span className="font-lato text-[#388e3c] text-[14px] leading-none">{num}</span>
      </div>
      <div className="flex-shrink-0 w-auto h-[157px] sm:h-[200px]">
        <img alt="" className="w-auto h-[157px] object-contain" src={img} />
      </div>
      <div className="relative flex-1 pt-[20px]">
        <p className="font-montserrat font-semibold text-[#050000] text-[clamp(0.9rem,1.1vw,1.1rem)] leading-[1.3] mb-[6px]">{title}</p>
        <p className="font-lato text-[#333] text-[clamp(0.8rem,0.95vw,0.95rem)] leading-[1.4]">{desc}</p>
      </div>
    </div>
  );
}

function HowItWorks() {
  const row1 = STEPS.slice(0, 3);
  const row2 = STEPS.slice(3);
  return (
    <div className="gap-[16px]">
      <p className="font-montserrat font-bold text-[#050000] text-[clamp(1.3rem,2vw,2rem)] text-center leading-[1.3] mb-[clamp(24px,4vw,48px)]">
        How It Works
      </p>
      <div className="flex flex-col xl:flex-row items-start gap-[16px] xl:gap-[16px]">
        {row1.map((step, i) => (
          <Fragment key={step.num}>
            <div className="flex-1 min-w-auto"><StepCard {...step} /></div>
            {i < row1.length - 1 && (
              <div className="hidden xl:flex flex-shrink-0 items-center justify-center w-[100px]">
                <img src={imgArrowRight} alt="" className="w-[103px] h-[23px] object-contain" style={{ marginTop: '60%' }} />
              </div>
            )}
          </Fragment>
        ))}
      </div>
      <div className="hidden xl:flex items-center justify-end pr-[calc(350px/2-11px)] w-full h-[100px]">
        <img src={imgArrowDown} alt="" className="w-[23px] h-[103px] object-contain" />
      </div>
      <div className="flex flex-col xl:flex-row-reverse items-start gap-[16px] xl:gap-[16px]">
        {[...row2].reverse().map((step, i, arr) => (
          <Fragment key={step.num}>
            <div className="flex-1 min-w-auto"><StepCard {...step} /></div>
            {i < arr.length - 1 && (
              <div className="hidden xl:flex flex-shrink-0 items-center justify-center w-[100px]">
                <img src={imgArrowLeft} alt="" className="w-[103px] h-[23px] object-contain" style={{ marginTop: '60%' }} />
              </div>
            )}
          </Fragment>
        ))}
        <div className="flex-shrink min-w-[150px]"></div>
      </div>
    </div>
  );
}

function UnlockRow() {
  return (
    <div className="flex flex-col sm:flex-row items-center gap-[clamp(16px,4vw,60px)] py-[20px]">
      <div className="flex flex-col gap-[12px] flex-shrink">
        <p className="font-montserrat font-bold text-[#050000] text-[clamp(1.2rem,2vw,2rem)] leading-[1.3]">
          Unlock the Value of Your Parking Assets
        </p>
        <p className="font-lato text-[#333] text-[clamp(0.95rem,1.2vw,1.25rem)] leading-[1.3]">
          Turn idle areas into sustainable, revenue-generating hubs for autonomous mobility
        </p>
      </div>
      <div className="flex-shrink-0">
        <img src={imgTechUnlock} alt="Unlock value illustration" className="w-[110px] h-[88px] object-contain" />
      </div>
    </div>
  );
}

function FleetCard({ icon, title, desc }) {
  return (
    <div className="flex flex-col gap-[16px] items-start">
      <img src={icon} alt={title} className="w-[40px] h-[40px] object-contain" />
      <div className="flex flex-col gap-[10px]">
        <p className="font-lato text-white text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3]">{title}</p>
        <p className="font-lato text-[#898989] text-[clamp(0.9rem,1vw,1.1rem)] leading-[1.3]">{desc}</p>
      </div>
    </div>
  );
}

const FLEET_CARDS = [
  { icon: iconUtilization, title: "Increase Fleet Utilization", desc: "Keep vehicles closer to passengers with real-time parking intelligence that maximizes uptime and reduces idle driving." },
  { icon: iconReduceCosts, title: "Reduce Operating Costs", desc: "Lower deadhead miles and minimize unnecessary repositioning with distributed staging locations across the city." },
  { icon: iconScaleInfra, title: "Scale Without Building Infrastructure", desc: "Expand into new markets using existing parking assets instead of investing in dedicated depots and long-term leases." },
  { icon: iconApiLocations, title: "One API. Thousands of Parking Locations", desc: "Programmatically find, reserve, and pay for parking through a single integration while GeoParking handles the complexity of parking inventory." },
];

function GreenBanner() {
  return (
    <div className="flex gap-[24px] items-start px-[32px] py-[16px] rounded-[10px]"
      style={{ background: "linear-gradient(90deg, rgba(56,142,60,1) 0%, rgba(56,142,60,1) 100%)" }}>
      <div className="flex-shrink-0 mt-[2px]">
        <img src={iconGroupNetwork} alt="Network" className="w-[32px] h-[32px] object-contain" />
      </div>
      <p className="font-lato text-white text-[clamp(0.95rem,1.1vw,1.25rem)] leading-[1.3]">
        As autonomous fleets grow, they need thousands of distributed parking locations, not just centralized depots. GeoParking helps property owners meet that demand while creating new revenue opportunities.
      </p>
    </div>
  );
}

function ShapeTheFuture() {
  return (
    <div className="flex flex-col lg:flex-row gap-[32px] items-stretch">
      <div className="flex-1 rounded-[10px] overflow-hidden relative min-h-[525px] border-[1px] border-[#5C5C5C]">
        <img alt="" className="absolute inset-0 w-full h-full object-cover" src={imgShape} />
        <div className="absolute inset-0 bg-[rgba(0,0,0,0.55)]" />
        <div className="relative z-10 p-[24px] flex flex-col gap-[20px] h-full justify-end">
          <div className="flex gap-[16px] items-center">
            <div className="flex items-center justify-center rounded-[8px] size-[48px]" style={{ background: "rgba(67,214,81,0.2)", border: "1px solid #43D651" }}>
              <img src={iconLeafFuture} alt="Shape the Future" className="w-[36px] h-[36px] object-contain" />
            </div>
            <div className="flex flex-col gap-[4px]">
              <p className="font-google-sans font-medium text-white text-[clamp(1.1rem,1.4vw,1.4rem)] leading-normal" style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>Shape the Future of Mobility</p>
              <div className="bg-[#76e688] h-[3px] w-[41px]" />
            </div>
          </div>
          <ul className="font-lato text-white text-[clamp(0.9rem,1vw,1rem)] leading-[1.3] list-disc space-y-[9px]">
            <li className="mb-1">Join the growing network supporting the next generation of autonomous transportation.</li>
            <li className="mb-1">Help accelerate the adoption of electric robotaxi fleets while reducing unnecessary traffic by eliminating empty trips back to distant depots.</li>
            <li className="mb-1">Contribute to cleaner, smarter cities while putting existing parking infrastructure to better use.</li>
          </ul>
        </div>
      </div>
      <div className="flex-1 rounded-[10px] overflow-hidden relative min-h-[525px] border-[1px] border-[#5C5C5C]">
        <img alt="" className="absolute inset-0 w-full h-full object-cover" src={imgUnlock} />
        <div className="absolute inset-0 bg-[rgba(0,0,0,0.55)]" />
        <div className="relative z-10 p-[24px] flex flex-col gap-[20px] h-full justify-end">
          <div className="flex gap-[16px] items-center">
            <div className="flex items-center justify-center rounded-[8px] size-[48px]" style={{ background: "rgba(1,79,164,0.2)", border: "1px solid #0A66C2" }}>
              <img src={iconBarChart} alt="Revenue" className="w-[30px] h-[28px] object-contain" />
            </div>
            <div className="flex flex-col gap-[4px]">
              <p className="font-google-sans font-medium text-white text-[clamp(1.1rem,1.4vw,1.4rem)] leading-normal" style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>Unlock Effortless Revenue</p>
              <div className="bg-[#76e688] h-[3px] w-[41px]" />
            </div>
          </div>
          <ul className="font-lato text-white text-[clamp(0.9rem,1vw,1rem)] leading-[1.3] list-disc space-y-[9px]">
            <li className="mb-1">Monetize parking spaces that would otherwise sit empty in your off peak hours.</li>
            <li className="mb-1">View and transfer your earnings directly from your Property Owner Dashboard.</li>
            <li className="mb-1">No hardware installation required for parking.</li>
            <li className="mb-1">10-minute Onboarding.</li>
          </ul>
        </div>
      </div>
      <div className="flex-1 rounded-[10px] overflow-hidden relative min-h-[525px] border-[1px] border-[#5C5C5C]">
        <img alt="" className="absolute inset-0 w-full h-full object-cover" src={imgStay} />
        <div className="absolute inset-0 bg-[rgba(0,0,0,0.55)]" />
        <div className="relative z-10 p-[24px] flex flex-col gap-[20px] h-full justify-end">
          <div className="flex gap-[16px] items-center">
            <div className="flex items-center justify-center rounded-[8px] size-[48px]" style={{ background: "rgba(151,71,255,0.2)", border: "1px solid #B075FE" }}>
              <img src={iconControlShield} alt="Control" className="w-[30px] h-[28px] object-contain" />
            </div>
            <div className="flex flex-col gap-[4px]">
              <p className="font-google-sans font-medium text-white text-[clamp(1.1rem,1.4vw,1.4rem)] leading-normal" style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>Stay in Control</p>
              <div className="bg-[#76e688] h-[3px] w-[41px]" />
            </div>
          </div>
          <ul className="font-lato text-white text-[clamp(0.9rem,1vw,1rem)] leading-[1.3] list-disc space-y-[9px]">
            <li className="mb-1">Choose exactly which spaces participate.</li>
            <li className="mb-1">Set available hours around your business operations.</li>
            <li className="mb-1">Update availability anytime through your dashboard.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function FleetSection() {
  return (
    <section className="bg-[#121212] py-[clamp(40px,6vw,80px)]">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col gap-[clamp(32px,4vw,60px)]">
          <div className="tech-reveal flex flex-col gap-[12px]">
            <p className="font-google-sans font-medium text-white text-[clamp(1.5rem,2.5vw,2.5rem)] leading-[1.3] py-[clamp(3vw,4vw,0vw)]" style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
              Robotaxis
            </p>
            <p className="font-montserrat font-bold text-white text-[clamp(1.4rem,2.5vw,2rem)] leading-[1.3] max-w-[1400px]">
              Maximize Your Robotaxi Fleet Efficiency
            </p>
            <p className="font-lato text-[#6b6b6b] text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.4] max-w-[1400px]">
              A connected, data-driven parking ecosystem built to keep autonomous fleets moving—faster, cleaner, and with greater operational reliability.
            </p>
          </div>
          <div className="flex w-full items-center">
            <div className="flex-shrink w-[35%] px-[clamp(16px,4vw,60px)] flex items-center justify-center">
              <div className="rounded-full overflow-hidden w-full max-w-[450px] aspect-square flex items-center justify-center">
                <img alt="Robotaxi fleet parking" className="w-full h-full object-cover rounded-full" src={imgMaximize} />
              </div>
            </div>
            <div className="tech-reveal w-[65%] px-[clamp(16px,4vw,60px)] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-[clamp(24px,3vw,60px)]">
              {FLEET_CARDS.map((card) => (
                <FleetCard key={card.title} {...card} />
              ))}
            </div>
          </div>
          <div className="tech-reveal"><GreenBanner /></div>
          <div className="tech-reveal"><ShapeTheFuture /></div>
        </div>
      </div>
    </section>
  );
}

function PropertyOwnersSection() {
  return (
    <section className="bg-white py-[clamp(40px,6vw,80px)]">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col gap-[clamp(32px,4vw,60px)]">
          <div className="tech-reveal">
            <p className="font-google-sans font-medium text-black text-[clamp(1.6rem,2.8vw,2.5rem)] leading-[1.3]" style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
              Property Owners
            </p>
          </div>
          <div className="tech-reveal"><HowItWorks /></div>
          <div className="tech-reveal bg-[#ebebeb] rounded-[8px] w-[80%] center m-auto p-[clamp(42px,3vw,42px)] flex">
            <div className="w-[50%] justify-center gap-[16px] flex">
              <p className="font-montserrat font-semibold text-[#050000] text-[clamp(1rem,1.4vw,1.5rem)] leading-[1.3]">
                Manage your parking availability, track earnings, and view your property's performance through the GeoParking platform.
              </p>
            </div>
            <div className="w-[50%] justify-center items-center gap-[16px]">
              <img alt="" className="w-full h-auto object-cover" src={imgFleetCard} />
            </div>
          </div>
          <div className="tech-reveal"><UnlockRow /></div>
        </div>
      </div>
    </section>
  );
}

export default function TechnologyPage() {
  useReveal();
  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      <Header />
      <main>
        {/* Hero */}
        <div className="pt-20">
          <div className="relative w-full overflow-hidden" style={{ height: "140px" }}>
            <div className="absolute inset-0 hero-bg-animate" style={{ transformOrigin: "center top" }}>
              <img alt="" className="absolute inset-0 max-w-none pointer-events-none size-full object-cover" src={imgHeroBg} />
            </div>
            <div className="absolute inset-0 bg-[rgba(255,255,255,0.8)]" />
            <div className="relative z-10 max-w-[1400px] mx-auto px-6 h-full flex items-center">
              <p className="hero-title-animate font-montserrat font-bold text-black text-[clamp(1.4rem,3.2vw,2.75rem)] leading-[1.3]">
                Innovation That Powers Every Space
              </p>
            </div>
          </div>
        </div>
        <PropertyOwnersSection />
        <FleetSection />
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}
